import {
  Box,
  Typography,
  useTheme,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Avatar,
  LinearProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { toast } from "react-toastify";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const CLOUDINARY_IMAGE_URL =
  "https://api.cloudinary.com/v1_1/dcmr4gnq5/image/upload";
const IMAGE_UPLOAD_PRESET = "book_uploads";

const uploadToCloudinary = async (file) => {
  const data = new FormData();
  data.append("file", file);
  data.append("upload_preset", IMAGE_UPLOAD_PRESET);
  data.append("cloud_name", "dcmr4gnq5");
  const res = await fetch(CLOUDINARY_IMAGE_URL, { method: "POST", body: data });
  if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
  const uploaded = await res.json();
  if (uploaded.error) throw new Error(uploaded.error.message);
  if (!uploaded.secure_url) throw new Error("No URL returned");
  return uploaded.secure_url;
};

/* ── Cover Upload Widget ─────────────────────────────── */
const CoverUpload = ({
  currentUrl,
  newFile,
  onFileChange,
  uploading,
  colors,
  isLight,
}) => {
  const preview = newFile ? URL.createObjectURL(newFile) : currentUrl;

  const uploadBg = isLight ? "#ffffff" : colors.primary[500];
  const uploadBorder = isLight ? "#d1d5db" : colors.primary[300];

  return (
    <Box>
      <Typography
        sx={{
          fontSize: "11px",
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: colors.greenAccent[isLight ? 500 : 400],
          mb: 1.5,
        }}
      >
        Cover Image
      </Typography>

      <label
        htmlFor="edit-cover-input"
        style={{ cursor: uploading ? "not-allowed" : "pointer" }}
      >
        <Box
          sx={{
            width: "100%",
            aspectRatio: "3/4",
            borderRadius: "12px",
            border: `2px dashed ${newFile ? colors.greenAccent[400] : uploadBorder}`,
            backgroundColor: uploadBg,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            position: "relative",
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: colors.greenAccent[400],
              "& .overlay": { opacity: 1 },
            },
          }}
        >
          {preview ? (
            <>
              <img
                src={preview}
                alt="Cover"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              {/* Hover overlay */}
              <Box
                className="overlay"
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  opacity: 0,
                  transition: "opacity 0.2s ease",
                  background: "rgba(0,0,0,0.55)",
                  backdropFilter: "blur(2px)",
                }}
              >
                <CloudUploadIcon sx={{ color: "#fff", fontSize: 32 }} />
                <Typography fontSize="12px" fontWeight={700} color="#fff">
                  Click to replace
                </Typography>
              </Box>
            </>
          ) : (
            <Box textAlign="center" p={3}>
              <Avatar
                sx={{
                  bgcolor: isLight ? "#d1fae5" : colors.greenAccent[700],
                  width: 52,
                  height: 52,
                  mx: "auto",
                  mb: 1.5,
                }}
              >
                <CloudUploadIcon
                  sx={{
                    color: isLight
                      ? colors.greenAccent[600]
                      : colors.greenAccent[300],
                  }}
                />
              </Avatar>
              <Typography
                fontSize="12px"
                fontWeight={600}
                color={isLight ? "#374151" : colors.grey[200]}
              >
                Click to upload
              </Typography>
              <Typography
                fontSize="10px"
                color={isLight ? "#9ca3af" : colors.grey[400]}
                mt={0.5}
              >
                JPEG, PNG, WebP · Max 10MB
              </Typography>
            </Box>
          )}
        </Box>
      </label>
      <input
        id="edit-cover-input"
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={onFileChange}
        disabled={uploading}
        hidden
      />

      {newFile && (
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          mt={1.5}
          p={1.5}
          sx={{
            backgroundColor: isLight ? "#f0fdf4" : colors.primary[500],
            borderRadius: "8px",
            border: `1px solid ${isLight ? "#86efac" : colors.greenAccent[700]}`,
          }}
        >
          <CheckCircleIcon
            sx={{
              color: isLight ? "#16a34a" : colors.greenAccent[400],
              fontSize: 15,
            }}
          />
          <Typography
            fontSize="11px"
            color={isLight ? "#166534" : colors.greenAccent[300]}
            noWrap
          >
            {newFile.name}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

/* ── Main Component ──────────────────────────────────── */
const Books = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isLight = theme.palette.mode === "light";
  const location = useLocation();

  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editBook, setEditBook] = useState(null);
  const [deleteBook, setDeleteBook] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newCoverFile, setNewCoverFile] = useState(null);

  const query = new URLSearchParams(location.search).get("q")?.trim() || "";

  const authHeader = useCallback(
    () => ({
      headers: {
        Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
      },
    }),
    [],
  );

  const fetchBooks = useCallback(async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/all-books`,
        authHeader(),
      );
      setBooks(response.data.books);
    } catch (err) {
      console.error("Failed to fetch books:", err);
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  const openEdit = (row) => {
    setEditBook(row);
    setNewCoverFile(null); // reset any previous pick
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const valid = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!valid.includes(file.type)) {
      toast.error("Please select JPEG, PNG, or WebP");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10MB");
      return;
    }
    setNewCoverFile(file);
  };

  const handleEditSave = useCallback(async () => {
    if (!editBook) return;
    setSaving(true);
    try {
      let coverImage = editBook.coverImage;

      if (newCoverFile) {
        toast.info("Uploading new cover image...");
        coverImage = await uploadToCloudinary(newCoverFile);
      }

      await axios.put(
        `${BASE_URL}/api/admin/books/${editBook._id}`,
        { ...editBook, coverImage },
        authHeader(),
      );

      toast.success("Book updated!");
      setEditBook(null);
      setNewCoverFile(null);
      fetchBooks();
    } catch (err) {
      console.error("Failed to update book:", err);
      toast.error(err.message || "Failed to update book");
    } finally {
      setSaving(false);
    }
  }, [editBook, newCoverFile, authHeader, fetchBooks]);

  const handleDelete = useCallback(async () => {
    if (!deleteBook) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${BASE_URL}/api/admin/books/${deleteBook._id}`,
        authHeader(),
      );
      setDeleteBook(null);
      fetchBooks();
    } catch (err) {
      console.error("Failed to delete book:", err);
    } finally {
      setDeleting(false);
    }
  }, [deleteBook, authHeader, fetchBooks]);

  /* ── DataGrid columns ── */
  const columns = [
    {
      field: "coverImage",
      headerName: "Cover",
      width: 80,
      renderCell: (params) => (
        <img
          src={params.row.coverImage}
          alt={params.row.title}
          style={{ width: 40, height: 55, objectFit: "cover", borderRadius: 4 }}
        />
      ),
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    { field: "author", headerName: "Author", flex: 1 },
    {
      field: "price",
      headerName: "Price (KES)",
      flex: 1,
      renderCell: (p) => (
        <Typography color={colors.greenAccent[500]}>
          KES {p.row.price}
        </Typography>
      ),
    },
    { field: "category", headerName: "Category", flex: 1 },
    {
      field: "format",
      headerName: "Format",
      flex: 1,
      renderCell: (p) => (
        <Chip
          label={p.row.format}
          size="small"
          sx={{
            backgroundColor:
              p.row.format === "Digital"
                ? colors.blueAccent[700]
                : p.row.format === "Print"
                  ? "#b71c1c"
                  : colors.greenAccent[700],
            color: colors.grey[100],
          }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (p) => (
        <Box display="flex" gap="8px">
          <Button
            variant="contained"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => openEdit(p.row)}
            sx={{ backgroundColor: colors.blueAccent[700] }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteBook(p.row)}
            sx={{ backgroundColor: "#b71c1c" }}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  const filteredBooks = useMemo(() => {
    if (!query) return books;
    const q = query.toLowerCase();
    return books.filter((b) =>
      [b.title, b.author, b.category, b.format, b.description]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [books, query]);

  /* ── Shared dialog styles ── */
  const dlgBg = isLight ? "#f9fafb" : colors.primary[400];
  const inputBg = isLight ? "#ffffff" : colors.primary[500];
  const inputText = isLight ? "#111827" : colors.grey[100];
  const inputLabel = isLight ? "#4b5563" : colors.grey[300];
  const inputBorder = isLight ? "#d1d5db" : colors.primary[300];

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: inputBg,
      "& fieldset": { borderColor: inputBorder },
      "&:hover fieldset": { borderColor: colors.greenAccent[400] },
      "&.Mui-focused fieldset": { borderColor: colors.greenAccent[400] },
    },
    "& .MuiInputLabel-root": { color: inputLabel },
    "& .MuiInputLabel-root.Mui-focused": {
      color: colors.greenAccent[isLight ? 500 : 400],
    },
    "& .MuiInputBase-input": { color: inputText },
    "& .MuiSelect-icon": { color: inputLabel },
  };

  return (
    <Box m="20px">
      <Header title="BOOKS" subtitle="Manage Your Book Catalog" />
      {query && (
        <Typography color={colors.grey[300]} mt="8px">
          Search results for "{query}" ({filteredBooks.length})
        </Typography>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" mt="100px">
          <CircularProgress />
        </Box>
      ) : (
        <Box
          m="40px 0 0 0"
          height="75vh"
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-cell": { borderBottom: "none" },
            "& .name-column--cell": { color: colors.greenAccent[300] },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.blueAccent[700],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.blueAccent[700],
            },
            "& .MuiCheckbox-root": {
              color: `${colors.greenAccent[200]} !important`,
            },
          }}
        >
          <DataGrid
            rows={filteredBooks.map((b) => ({ ...b, id: b._id }))}
            columns={columns}
            rowHeight={65}
          />
        </Box>
      )}

      {/* ══════════════ EDIT DIALOG ══════════════ */}
      <Dialog
        open={!!editBook}
        onClose={() => {
          setEditBook(null);
          setNewCoverFile(null);
        }}
        maxWidth="md"
        fullWidth
      >
        {saving && (
          <LinearProgress
            sx={{
              "& .MuiLinearProgress-bar": {
                backgroundColor: colors.greenAccent[400],
              },
              backgroundColor: isLight ? "#d1fae5" : colors.primary[300],
            }}
          />
        )}

        <DialogTitle
          sx={{ backgroundColor: dlgBg, fontWeight: 700, fontSize: "16px" }}
        >
          Edit Book
        </DialogTitle>

        <DialogContent sx={{ backgroundColor: dlgBg, pt: "20px !important" }}>
          {/* Two-column layout: cover left, fields right */}
          <Box
            display="grid"
            gridTemplateColumns="220px 1fr"
            gap="28px"
            mt="4px"
          >
            {/* LEFT — cover upload */}
            <CoverUpload
              currentUrl={editBook?.coverImage}
              newFile={newCoverFile}
              onFileChange={handleCoverChange}
              uploading={saving}
              colors={colors}
              isLight={isLight}
            />

            {/* RIGHT — text fields */}
            <Box display="flex" flexDirection="column" gap="16px">
              <TextField
                label="Title"
                value={editBook?.title || ""}
                onChange={(e) =>
                  setEditBook((p) => ({ ...p, title: e.target.value }))
                }
                fullWidth
                sx={inputSx}
              />

              <TextField
                label="Author"
                value={editBook?.author || ""}
                onChange={(e) =>
                  setEditBook((p) => ({ ...p, author: e.target.value }))
                }
                fullWidth
                sx={inputSx}
              />

              <Box display="grid" gridTemplateColumns="1fr 1fr" gap="16px">
                <TextField
                  label="Price (KES)"
                  type="number"
                  value={editBook?.price || ""}
                  onChange={(e) =>
                    setEditBook((p) => ({
                      ...p,
                      price: Number(e.target.value),
                    }))
                  }
                  fullWidth
                  sx={inputSx}
                />

                <TextField
                  label="Category"
                  value={editBook?.category || ""}
                  onChange={(e) =>
                    setEditBook((p) => ({ ...p, category: e.target.value }))
                  }
                  fullWidth
                  sx={inputSx}
                />
              </Box>

              <TextField
                label="Format"
                select
                value={editBook?.format || "Digital"}
                onChange={(e) =>
                  setEditBook((p) => ({ ...p, format: e.target.value }))
                }
                fullWidth
                sx={inputSx}
              >
                <MenuItem value="Digital">Digital</MenuItem>
                <MenuItem value="Print">Print</MenuItem>
                <MenuItem value="Both">Both</MenuItem>
              </TextField>

              <TextField
                label="Description"
                value={editBook?.description || ""}
                onChange={(e) =>
                  setEditBook((p) => ({ ...p, description: e.target.value }))
                }
                fullWidth
                multiline
                rows={5}
                sx={inputSx}
              />

              <Typography
                fontSize="11px"
                color={isLight ? "#9ca3af" : colors.grey[400]}
                textAlign="right"
                mt={-1}
              >
                {(editBook?.description || "").length} characters
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ backgroundColor: dlgBg, px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => {
              setEditBook(null);
              setNewCoverFile(null);
            }}
            sx={{
              color: isLight ? "#6b7280" : colors.grey[300],
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditSave}
            disabled={saving}
            variant="contained"
            sx={{
              backgroundColor: colors.greenAccent[isLight ? 500 : 600],
              color: isLight ? "#fff" : colors.primary[900],
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              "&:hover": {
                backgroundColor: colors.greenAccent[isLight ? 400 : 500],
              },
            }}
          >
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════════════ DELETE DIALOG ══════════════ */}
      <Dialog
        open={!!deleteBook}
        onClose={() => setDeleteBook(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: dlgBg }}>Delete Book</DialogTitle>
        <DialogContent sx={{ backgroundColor: dlgBg }}>
          <Typography mt="8px">
            Are you sure you want to delete{" "}
            <strong>"{deleteBook?.title}"</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: dlgBg }}>
          <Button
            onClick={() => setDeleteBook(null)}
            sx={{ color: colors.grey[100], textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            variant="contained"
            sx={{
              backgroundColor: "#b71c1c",
              textTransform: "none",
              borderRadius: "8px",
            }}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Books;
